import React from 'react';
import { Link } from 'react-router-dom';
import { useSeoHead } from '../hooks/useSeoHead';
import { articleCrumbs, breadcrumbJsonLd, getTopic } from '../articles';
import { Breadcrumb, FitCheckBox, MoreTopics, QuickLink } from '../components/articles/ArticleParts';
import { SvsVorschreibungMock } from '../components/svs-guide/SvsVorschreibungMock';
import { GuideLanguageSwitch } from '../components/svs-guide/GuideLanguageSwitch';
import { SvsEstimateCalculator } from '../components/svs-guide/SvsEstimateCalculator';
import '../components/svs-guide/svs-guide.css';

// Published article (src/articles.ts) — prerenderRoutes.ts emits the
// matching robots tag for the static HTML.
const INDEXABLE = true;

const TOPIC = getTopic('svs');
const ARTICLE = TOPIC.en;
const CRUMBS = articleCrumbs(TOPIC, 'en');
const PATH = ARTICLE.path;
const CANONICAL = `https://pstax.at${PATH}`;
// Exported so prerenderRoutes.ts uses the same title, description and
// Article JSON-LD as the client-side head (no second copy to drift apart).
export const TITLE_EN = ARTICLE.title;
export const DESCRIPTION_EN = ARTICLE.description;
const H1 = ARTICLE.h1;

const AUTHOR_PERSON = {
  '@type': 'Person',
  name: 'Pascal Schraml',
  jobTitle: 'Certified tax advisor (Steuerberater)',
  worksFor: { '@type': 'Organization', name: 'PSTax Steuerberatung FlexCo', url: 'https://pstax.at' },
};

export const ARTICLE_JSONLD_EN = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: H1,
  datePublished: ARTICLE.datePublished,
  dateModified: ARTICLE.dateModified,
  inLanguage: 'en',
  author: AUTHOR_PERSON,
  reviewedBy: AUTHOR_PERSON,
  publisher: { '@type': 'Organization', name: 'PSTax Steuerberatung FlexCo', url: 'https://pstax.at' },
  about: 'Sozialversicherungsanstalt der Selbständigen',
};

const TOC_ITEMS: { label: string; id: string }[] = [
  { label: 'What is the SVS?', id: 'what' },
  { label: 'Do I have to pay SVS?', id: 'who' },
  { label: 'How much is SVS in 2026?', id: 'cost' },
  { label: 'Why year one looks cheap', id: 'first-years' },
  { label: 'SVS back payment: when it comes and how to calculate it', id: 'back-payment' },
  { label: 'When do you pay?', id: 'when' },
  { label: 'What you get for the money', id: 'value' },
  { label: 'If you earn very little', id: 'low-income' },
  { label: 'Too high, or too low?', id: 'too-high' },
  { label: 'SVS and income tax', id: 'tax' },
  { label: 'Five common mistakes', id: 'mistakes' },
  { label: 'If you moved here recently', id: 'moved-here' },
  { label: 'Frequently asked questions', id: 'faq' },
  { label: 'Official sources', id: 'sources' },
];

const AT_A_GLANCE: [string, string][] = [
  ['Who pays', 'Almost every self-employed person in Austria'],
  ['Minimum contribution base', '€551.10 / month'],
  ['Maximum contribution base', '€8,085.00 / month'],
  ['Lowest possible contribution', '€160.81 / month'],
  ['Paid', '4× a year, or monthly'],
  ['Income limit, Neue Selbständige', '€6,613.20 / year'],
  ['The catch', 'Bills are recalculated years later'],
];

// Exported so prerenderRoutes.ts can build the FAQPage JSON-LD from the same
// source instead of duplicating the answers as a second copy of the text.
// Most answers render exactly their plain text (`plainFaq`), so the visible
// answer and the JSON-LD answer can't drift apart.
const plainFaq = (q: string, aPlain: string) => ({ q, aPlain, a: <>{aPlain}</> });

export const FAQS_EN: { q: string; a: React.ReactNode; aPlain: string }[] = [
  plainFaq(
    'How much SVS do I pay as a freelancer in Austria?',
    'Roughly 27 % of your contribution base: in 2026 that is 18.5 % pension, 6.8 % health and 1.53 % self-employed provision, plus €12.95 a month accident insurance. The minimum is €160.81 a month, the maximum about €26,200 a year. As a rule of thumb with flat-rate expenses (Basis- or Kleinunternehmerpauschalierung) and the profit allowance, profit is about 70 % of revenue, so SVS ends up at about 20 % of revenue — and together with income tax you should set aside about a third of your profit.',
  ),
  plainFaq(
    'What is the minimum SVS contribution in 2026?',
    'The minimum SVS contribution in 2026 is €160.81 a month, which is €482.43 a quarter or €1,929.72 a year. It is 26.83 % of the minimum contribution base of €551.10 a month, plus €12.95 accident insurance. Only Neue Selbständige below the income limit (nothing) and trade-licence holders with the small-business exemption (accident insurance only) pay less.',
  ),
  plainFaq(
    'Do I have to pay SVS if I earn very little?',
    'With a trade licence, yes — the minimum contribution is due even with zero revenue, unless you qualify for the small-business exemption (in 2026: profit up to €6,613.20 and turnover up to €55,000, open only to certain groups such as recent starters). Without a trade licence (Neue Selbständige), you are only compulsorily insured once your profit passes €6,613.20 a year. If you go over that limit, report it yourself with an Überschreitungserklärung, or the SVS adds a 9.3 % surcharge.',
  ),
  plainFaq(
    'Do I have to register with the SVS myself?',
    'If you registered a trade licence, no — the WKO passes your details to the SVS and insurance starts automatically. As a Neue:r Selbständige:r you have to register yourself, within one month of starting, if you expect to pass the income limit. In both cases you also need to register with the tax office separately — registration with one authority is not registration with the other.',
  ),
  plainFaq(
    'Why did the SVS send me a back-payment?',
    'Because your earlier bills were only provisional. The SVS bills a provisional amount — the minimum if you are new, otherwise based on your income from three years earlier — and recalculates once your income tax assessment shows your real profit (the Nachbemessung). The difference is charged as a back-payment; it is not a mistake or a penalty, it is how the system works.',
  ),
  plainFaq(
    'When will the SVS back-payment arrive?',
    'Without any action, usually in the third year: the income tax assessment for 2026 arrives in 2027, and the SVS then charges the back-payment for 2026 in 2028, split across the four quarters. Once the assessment is there, an early final settlement can be requested from the SVS, so the year is settled in the following year instead, typically around the second quarter.',
  ),
  plainFaq(
    'Are SVS contributions tax-deductible?',
    'Yes, SVS contributions are business expenses — including back-payments — deductible in the year you pay them (with cash-basis accounting). They are deductible on top of flat-rate expenses such as the Basispauschalierung or Kleinunternehmerpauschalierung. So a large back-payment you pay in 2028 reduces your 2028 income tax, not the tax for the year it relates to.',
  ),
  {
    q: 'Should I prepay SVS before the end of the year to save tax?',
    aPlain:
      'Sometimes — but in most cases a prepayment only shifts tax and SVS in time rather than saving anything. It is deductible in the year you pay it if it is based on a careful estimate, and it sits as a credit on your SVS account that the SVS automatically sets off against your next bills — so you must keep paying your quarterly bills as usual. It really pays off when this year’s income is clearly higher than the following years’ (for example, closing the business, parental leave, time abroad); with a profit under about €20,000 it is rarely urgent.',
    a: (
      <>
        Sometimes — but in most cases a prepayment only shifts tax and SVS in time rather than saving anything. It is
        deductible in the year you pay it if it is based on a careful estimate, and it sits as a credit on your SVS
        account that the SVS automatically sets off against your next bills — so you must keep paying your quarterly
        bills as usual. It really pays off when this year’s income is clearly higher than the following years’ (for
        example, closing the business, parental leave, time abroad); with a profit under about €20,000 it is rarely
        urgent. More:{' '}
        <Link to={TOPIC.en.shortPath} className="text-pstax-link hover:underline">
          SVS prepayment in short
        </Link>
        .
      </>
    ),
  },
  plainFaq(
    'A back-payment arrived that I cannot pay. What now?',
    'Ask the SVS for instalments or a deferral before the due date — this is usually granted. Back-payments are already split across the four quarters of the following year, and back-payments from your first three years of self-employment can be spread over twelve quarters on request. Do not simply let the due date pass: interest starts running, and an arrangement is much easier to agree before that than after.',
  ),
  plainFaq(
    'I have a job and freelance on the side. Do I pay twice?',
    'You get two bills — ASVG on your salary and SVS on your self-employed profit — and up to the cap you pay both in full. But the two contribution bases together are capped at the maximum base (€8,085 a month, €97,020 a year in 2026). The SVS takes this into account automatically and refunds excess pension contributions without an application; only accident insurance is paid twice.',
  ),
  plainFaq(
    'I was insured in another EU country before. What does that mean?',
    'Talk it through before you register, not after. Within the EU you are generally insured in only one country at a time, and which one depends on where you live, where you work and what you had before — including any activity still running in your old country. The order of your registrations and deregistrations can change what you end up paying, so bring it to your first consultation.',
  ),
  plainFaq(
    'Do I need to speak German to deal with the SVS?',
    'To deal with them yourself, realistically yes — correspondence, forms and most of the online portal are in German. Working with us, no. You forward what arrives, we handle the correspondence and explain the outcome in English.',
  ),
];

const ExternalLink: React.FC<{ href: string; children: React.ReactNode; className?: string }> = ({
  href,
  children,
  className = '',
}) => (
  <a href={href} target="_blank" rel="noopener" className={className}>
    {children}
    <span aria-hidden="true"> ↗</span>
    <span className="sr-only">, opens in a new tab</span>
  </a>
);

const Callout: React.FC<{ tone: 'blue' | 'orange'; label: string; children: React.ReactNode }> = ({
  tone,
  label,
  children,
}) => (
  <div
    className={`my-6 rounded-2xl border-l-4 p-5 ${
      tone === 'blue' ? 'bg-pstax-blue/10 border-pstax-blue' : 'bg-pstax-orange/10 border-pstax-orange'
    }`}
  >
    <p
      className={`text-[11px] font-black uppercase tracking-wider mb-2 ${
        tone === 'blue' ? 'text-pstax-navy' : 'text-pstax-navy'
      }`}
    >
      {label}
    </p>
    <p className="text-pstax-navy leading-relaxed">{children}</p>
  </div>
);

const SoonBadge: React.FC = () => (
  <span className="inline-flex items-center text-[10px] font-black uppercase tracking-wider text-pstax-gray bg-pstax-border/60 rounded-full px-2 py-0.5 ml-2 align-middle">
    Soon
  </span>
);

export const SvsSocialInsuranceGuide: React.FC = () => {
  useSeoHead({
    title: TITLE_EN,
    description: DESCRIPTION_EN,
    canonical: CANONICAL,
    indexable: INDEXABLE,
    htmlLang: 'en',
    hreflang: [
      { lang: 'en', href: 'https://pstax.at/en/guides/svs-social-insurance/' },
      { lang: 'de', href: 'https://pstax.at/de/guides/svs-sozialversicherung/' },
      { lang: 'x-default', href: 'https://pstax.at/en/guides/svs-social-insurance/' },
    ],
    jsonLd: [
      ARTICLE_JSONLD_EN,
      breadcrumbJsonLd(CRUMBS, PATH),
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS_EN.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.aPlain },
        })),
      },
    ],
  });

  return (
    <div className="svs-guide pt-28 pb-24">
      <div className="container mx-auto px-6 max-w-6xl">
        {/* Breadcrumb */}
        <Breadcrumb crumbs={CRUMBS} lang="en" />

          <article className="min-w-0">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-pstax-blue mb-4">
              Articles · Social insurance
            </p>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-pstax-navy mb-4 leading-tight">
              {H1}
            </h1>
            <QuickLink topic={TOPIC} lang="en" />
            <p className="text-xl text-pstax-gray leading-relaxed mb-4">
              Everyone worries about the tax office. The expensive surprise usually comes from the SVS — because the
              first bills are only provisional, and nobody tells you that.
            </p>
            <div className="flex items-center gap-3 mb-4">
              <picture className="shrink-0">
                <source srcSet="/pascal-schraml.webp" type="image/webp" />
                <img
                  src="/pascal-schraml.jpg"
                  alt=""
                  width={36}
                  height={36}
                  decoding="async"
                  className="w-9 h-9 rounded-full object-cover"
                />
              </picture>
              <p className="text-sm text-pstax-gray">
                By{' '}
                <a href="#author" className="font-semibold text-pstax-navy hover:text-pstax-link">
                  Pascal Schraml
                </a>
                , certified tax advisor (<span lang="de">Steuerberater</span>) · Updated{' '}
                <time dateTime="2026-11-02">November 2026</time>
              </p>
            </div>
            <GuideLanguageSwitch from={PATH} targetLang="DE" label="Diese Seite auf Deutsch →" />

            {/* At a glance */}
            <div className="mt-10 mb-10 bg-white border border-pstax-border rounded-2xl shadow-sm p-6 sm:p-8">
              <h2 className="text-lg font-display font-bold text-pstax-navy mb-4">SVS 2026 at a glance</h2>
              <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {AT_A_GLANCE.map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-4 border-b border-pstax-border/70 py-1.5 text-sm">
                    <dt className="text-pstax-gray">{label}</dt>
                    <dd className="font-semibold text-pstax-navy text-right">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {/* Calculator promo */}
            <div className="mb-10 bg-pstax-blue/10 border border-pstax-blue/30 rounded-2xl p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-sm font-black uppercase tracking-wider text-pstax-navy mb-1">
                  Estimate your numbers
                </p>
                <p className="text-sm text-pstax-gray">
                  See roughly what income tax and SVS will take from your revenue this year.
                </p>
              </div>
              <Link
                to="/en/#calculator"
                className="btn-outline shrink-0 min-h-[44px] flex items-center justify-center whitespace-nowrap"
              >
                Open the calculator
              </Link>
            </div>

            {/* Table of contents */}
            <nav className="toc-top mb-12 bg-white border border-pstax-border rounded-2xl shadow-sm p-6 sm:p-8" aria-label="On this page">
              <p className="text-[11px] font-black uppercase tracking-wider text-pstax-gray mb-3">On this page</p>
              <ol className="grid min-[620px]:grid-cols-2 gap-x-8 gap-y-1">
                {TOC_ITEMS.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className="flex min-h-[32px] items-center text-sm text-pstax-navy hover:text-pstax-link py-1"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            {/* What is the SVS? */}
            <h2 id="what" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              What is the SVS?
            </h2>
            <p className="mb-4 leading-relaxed">
              SVS stands for <span lang="de">Sozialversicherungsanstalt der Selbständigen</span> — the social
              insurance institution for self-employed people in Austria. If you are employed, your social insurance
              runs through the ÖGK and your employer handles it. If you are self-employed, it runs through the SVS
              and you handle it yourself.
            </p>
            <p className="mb-4 leading-relaxed">
              SVS is <em>not</em> a tax. It is insurance, covering health, pension and accident insurance plus the
              self-employed provision.
            </p>
            <p className="mb-4 leading-relaxed">
              Two separate authorities, two separate bills: the <strong>Finanzamt</strong> takes your income tax, the{' '}
              <strong>SVS</strong> takes your social insurance. They talk to each other — which is exactly where the
              surprise comes from. More on that below.
            </p>

            {/* Do I have to pay SVS? */}
            <h2 id="who" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Do I have to pay SVS?
            </h2>
            <p className="mb-4 leading-relaxed">
              Almost certainly yes. Which rules apply to you depends on <em>how</em> you are self-employed, and there
              are two groups.
            </p>
            <h3 className="text-xl font-display font-bold text-pstax-navy mt-6 mb-3">
              1. With a trade licence <span lang="de">(Gewerbetreibende)</span>
            </h3>
            <p className="mb-4 leading-relaxed">
              If your activity needs a <span lang="de">Gewerbeschein</span> — IT services, photography, most
              consulting, personal training, many crafts — you become a member of the Austrian Economic Chamber
              (WKO) when you register. SVS insurance starts on the same day.{' '}
              <strong>As a rule, there is no income threshold:</strong> the minimum contribution applies even with
              zero revenue. Under certain conditions, however, you can apply to be exempted from most of the
              contributions (see{' '}
              <a href="#small-business-exemption" className="text-pstax-link hover:underline">
                the small-business exemption <span lang="de">(Kleinstunternehmerregelung)</span>
              </a>{' '}
              below).
            </p>
            <h3 className="text-xl font-display font-bold text-pstax-navy mt-6 mb-3">
              2. Without a trade licence <span lang="de">(Neue Selbständige)</span>
            </h3>
            <p className="mb-4 leading-relaxed">
              Some activities do not need a trade licence — writing, lecturing, translating, certain therapeutic and
              artistic work. These are called <span lang="de">Neue Selbständige</span>. Here an income limit applies:
              you only become compulsorily insured once your <strong>profit passes €6,613.20 in 2026</strong>.
            </p>
            <Callout tone="blue" label="Important">
              Which group you belong to is not your choice. It follows from the activity itself. Getting this wrong
              at the start is one of the more expensive mistakes, because it is corrected retroactively.
            </Callout>
            <p className="mb-4 leading-relaxed">
              Have a job <em>and</em> freelance on the side? Then you pay ASVG through your employer and SVS on your
              self-employed profit. You do not lose out: the two contribution bases together are capped at the
              maximum base. The SVS takes this into account automatically and refunds excess pension contributions
              without an application — only accident insurance is paid twice.
            </p>

            {/* How much */}
            <h2 id="cost" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              How much is SVS in 2026?
            </h2>
            <p className="mb-6 leading-relaxed">
              The rates are percentages of your <strong>contribution base</strong> <span lang="de">(Beitragsgrundlage)</span>.
              Everyone pays at least the minimum base, and nobody pays on more than the maximum base.
            </p>

            <div className="overflow-x-auto mb-3">
              <table className="w-full text-sm border-collapse">
                <caption className="text-left text-xs text-pstax-gray mb-3">
                  SVS rates 2026 and the contribution on the minimum base
                </caption>
                <thead>
                  <tr className="border-b-2 border-pstax-navy">
                    <th scope="col" className="text-left py-2 pr-4 font-bold text-pstax-navy">
                      Branch
                    </th>
                    <th scope="col" className="text-right py-2 pr-4 font-bold text-pstax-navy">
                      Rate 2026
                    </th>
                    <th scope="col" className="text-right py-2 font-bold text-pstax-navy">
                      On €551.10 / month
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-pstax-border">
                    <th scope="row" className="text-left py-2 pr-4 font-normal">
                      Pension <span lang="de">(PV)</span>
                    </th>
                    <td className="text-right py-2 pr-4">18.50 %</td>
                    <td className="text-right py-2">€101.95</td>
                  </tr>
                  <tr className="border-b border-pstax-border">
                    <th scope="row" className="text-left py-2 pr-4 font-normal">
                      Health <span lang="de">(KV)</span>
                    </th>
                    <td className="text-right py-2 pr-4">6.80 %</td>
                    <td className="text-right py-2">€37.48</td>
                  </tr>
                  <tr className="border-b border-pstax-border">
                    <th scope="row" className="text-left py-2 pr-4 font-normal">
                      Self-employed provision
                    </th>
                    <td className="text-right py-2 pr-4">1.53 %</td>
                    <td className="text-right py-2">€8.43</td>
                  </tr>
                  <tr className="border-b border-pstax-border">
                    <th scope="row" className="text-left py-2 pr-4 font-normal">
                      Accident <span lang="de">(UV)</span>
                    </th>
                    <td className="text-right py-2 pr-4">fixed amount</td>
                    <td className="text-right py-2">€12.95</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr className="font-bold text-pstax-navy">
                    <th scope="row" className="text-left py-2 pr-4">
                      Minimum total
                    </th>
                    <td className="text-right py-2 pr-4">—</td>
                    <td className="text-right py-2">€160.81 / month</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="text-sm text-pstax-gray mb-6 leading-relaxed">
              That is <strong>€482.43 per quarter</strong> or <strong>€1,929.72 per year</strong> — the least anyone
              pays in 2026. The maximum base is €8,085.00 per month (€97,020.00 per year); above that profit, your
              contributions stop growing.{' '}
              <ExternalLink
                href="https://www.wko.at/entlohnung/beitragswesen-selbstaendige-2026"
                className="text-pstax-link hover:underline"
              >
                WKO — Beitragswesen Selbständige 2026
              </ExternalLink>
            </p>

            <Callout tone="orange" label="Easy to miss">
              Your contribution base is <strong>not</strong> simply your profit. It is your profit <em>plus</em> the
              health and pension contributions the SVS charged you in that year — including back-payments for earlier
              years that were charged in that year. So the base is always somewhat
              higher than the number in your tax return — and so is the bill.
            </Callout>

            <p className="mb-4 leading-relaxed">
              Want a rough number for your own situation? Our{' '}
              <Link to="/en/#calculator" className="text-pstax-link hover:underline font-semibold">
                tax calculator
              </Link>{' '}
              estimates income tax, SVS and what is left over, from your revenue and expenses.
            </p>

            {/* First years */}
            <h2 id="first-years" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Your first years look cheap. That is the problem.
            </h2>
            <p className="mb-4 leading-relaxed">
              In your first three calendar years of self-employment, the SVS provisionally charges the minimum — €160.81
              per month — no matter what you earn. That feels like a gift. It is really a loan.
            </p>
            <ul className="mb-4 space-y-2 list-disc pl-6 leading-relaxed">
              <li>
                <strong>Health insurance, years 1 and 2 (with a trade licence):</strong> fixed at the minimum, and it is{' '}
                <em>never</em> recalculated — provided you were not insured under the GSVG in the ten years before.
                The same applies to the self-employed provision. This part really is a gift.
              </li>
              <li>
                <strong>Pension insurance, from day one:</strong> provisional. It is always recalculated later, for
                every year.
              </li>
              <li>
                <strong>From year 3 onwards:</strong> health insurance is recalculated too.
              </li>
            </ul>
            <p className="mb-4 leading-relaxed">
              So if you earn €50,000 in your first year and pay €1,929.72, you have not paid €1,929.72 for that year.
              You have paid a deposit.
            </p>

            {/* Nachbemessung */}
            <h2 id="back-payment" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              SVS back payment: when it comes and how to calculate it
            </h2>
            <p className="mb-6 leading-relaxed">
              The SVS back payment comes from the <span lang="de">Nachbemessung</span>, which has no good English
              translation — "retroactive reassessment" is close. It is the single most important thing to understand
              about the SVS, and it works in three steps.
            </p>

            <ol className="mb-8 space-y-4">
              <li className="bg-white border border-pstax-border rounded-2xl p-5 flex gap-4">
                <span className="shrink-0 w-8 h-8 rounded-full bg-pstax-navy text-white font-bold flex items-center justify-center">
                  1
                </span>
                <p className="leading-relaxed">
                  <strong>You pay a provisional amount</strong> — The SVS does not know this year's profit yet —
                  nobody does. So it bills you provisionally: the minimum in your first three years, from year four
                  your contribution base from three years earlier, adjusted upwards.
                </p>
              </li>
              <li className="bg-white border border-pstax-border rounded-2xl p-5 flex gap-4">
                <span className="shrink-0 w-8 h-8 rounded-full bg-pstax-navy text-white font-bold flex items-center justify-center">
                  2
                </span>
                <p className="leading-relaxed">
                  <strong>Your tax assessment arrives</strong> — You file your income tax return, the Finanzamt
                  issues the <span lang="de">Einkommensteuerbescheid</span>, and it forwards the figures to the SVS.
                  You typically receive the assessment for a year in the following year.
                </p>
              </li>
              <li className="bg-white border border-pstax-border rounded-2xl p-5 flex gap-4">
                <span className="shrink-0 w-8 h-8 rounded-full bg-pstax-navy text-white font-bold flex items-center justify-center">
                  3
                </span>
                <p className="leading-relaxed">
                  <strong>The SVS recalculates — and charges you twice over</strong> — The SVS charges the back-payment
                  for the old year in the following year, split across the four quarters. Left alone, you pay for year
                  one in year three. From year four, your <em>current</em> bills also follow your real profit — so for
                  a while you pay back-payments and higher current contributions at the same time.
                </p>
              </li>
            </ol>

            <div className="bg-pstax-navy text-white rounded-2xl p-6 sm:p-8 mb-8">
              <h3 className="text-lg font-display font-bold mb-1">What that looks like in numbers</h3>
              <p className="text-sm text-white/70 mb-5">
                Simplified example: first year of self-employment in 2026 with a trade licence, profit €45,000, no
                other income.
              </p>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between gap-4 border-b border-white/15 pb-3">
                  <dt>Paid to the SVS during 2026</dt>
                  <dd className="font-semibold">€1,929.72</dd>
                </div>
                <div className="flex justify-between gap-4 border-b border-white/15 pb-3">
                  <dt>Back-payment for 2026 (assessment 2027, charged in 2028 over four quarters)</dt>
                  <dd className="font-bold text-pstax-orange">≈ €7,400</dd>
                </div>
                <div className="flex justify-between gap-4 border-b border-white/15 pb-3">
                  <dt>Monthly bill before the reassessment</dt>
                  <dd className="font-semibold">€160.81</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt>Current monthly bill from 2029 (provisional, based on 2026)</dt>
                  <dd className="font-bold text-pstax-orange">≈ €1,050</dd>
                </div>
              </dl>
              <p className="text-xs text-white/60 mt-5 leading-relaxed">
                Rounded illustration based on the 2026 rates above, for one specific case. Your own numbers will
                differ. With a trade licence, health insurance and the self-employed provision are not reassessed
                for year one — only pension is. The 2029 monthly bill leaves out the annual indexation, and the
                back-payment for 2027 arrives on top in 2029.
              </p>
            </div>

            <p className="mb-4 leading-relaxed">
              Nothing has gone wrong in this example. Nobody made a mistake. This is simply how the system works —
              and it is why a self-employed person in Austria should always be holding money that does not feel like
              theirs.
            </p>
            <p className="mb-4 leading-relaxed">
              You do not have to wait until year three, though. Once the income tax assessment is there, an{' '}
              <strong>early final settlement</strong> <span lang="de">(vorzeitige Endabrechnung)</span> can be
              requested from the SVS. Year one is then settled around the second quarter of year two — the
              back-payment comes earlier, but it is predictable and several years do not pile up. For our clients we
              request this after the assessment, with their OK.
            </p>

            <SvsVorschreibungMock
              legendTitle="What the four markers show"
              legend={[
                <>Just one quarter. Three more bills follow within the same year.</>,
                <>The contribution base — every amount below is a percentage of this one number.</>,
                <>What you actually transfer. It feels like the price. It is a deposit.</>,
                <>
                  <strong>The line almost nobody reads.</strong> "Provisional" means: this amount gets recalculated
                  once your tax assessment arrives.
                </>,
              ]}
            />
            <p className="text-sm text-pstax-gray -mt-6 mb-10 leading-relaxed">
              Illustration of an SVS quarterly notice, rebuilt for this page with 2026 minimum figures — not a scan
              of a real document. The line that matters is the one most people skip.
            </p>

            <h3 id="calculate-back-payment" className="text-xl font-display font-bold text-pstax-navy mt-6 mb-3">
              How to calculate your SVS back payment — roughly, in four steps
            </h3>
            <ol className="mb-4 space-y-2 list-decimal pl-6 leading-relaxed">
              <li>
                <strong>Take the year's profit</strong> — from your bookkeeping or tax return.
              </li>
              <li>
                <strong>Take about 27 % of it.</strong> That is roughly the total SVS due for that year. More
                precisely: profit plus the health and pension contributions charged for that year, times 27 %. In
                your first two years with a trade licence only pension is reassessed — use 18.5 % instead.
              </li>
              <li>
                <strong>Subtract what you have already been charged for that year.</strong> Only contributions{' '}
                <em>for</em> that year count — what you paid this year may include back payments for earlier years.
              </li>
              <li>
                <strong>The rest is your approximate back payment.</strong> In the example above: (€45,000 + €1,673
                contributions charged) × 18.5 % − €1,223 pension already charged ≈ €7,400.
              </li>
            </ol>
            <p className="mb-4 leading-relaxed">
              Even rougher, if you do not know your profit yet: with flat-rate expenses (Basis- or
              Kleinunternehmerpauschalierung) SVS comes to about <strong>20 % of your revenue</strong> — again minus
              what has already been charged for that year. For a quick estimate, use the calculator below; for
              income tax and SVS together, our{' '}
              <Link to="/en/#calculator" className="text-pstax-link hover:underline font-semibold">
                tax calculator
              </Link>
              .
            </p>
            <SvsEstimateCalculator lang="en" />

            {/* When do you pay */}
            <h2 id="when" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              When do you pay?
            </h2>
            <p className="mb-4 leading-relaxed">The SVS bills quarterly. The due dates are always the same:</p>
            <ul className="mb-4 space-y-2 list-disc pl-6 leading-relaxed">
              <li>
                <strong>28 February</strong> — first quarter
              </li>
              <li>
                <strong>31 May</strong> — second quarter
              </li>
              <li>
                <strong>31 August</strong> — third quarter
              </li>
              <li>
                <strong>30 November</strong> — fourth quarter
              </li>
            </ul>
            <p className="mb-4 leading-relaxed">
              You can switch to monthly direct debit instead, which most of our clients find easier to plan around.
              Back-payments from a reassessment come on top, split across the four quarterly bills of the following
              year. Pay late and the SVS charges interest — so if money is tight, ask for instalments <em>before</em> the
              due date rather than letting it run.
            </p>

            {/* Value */}
            <h2 id="value" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              What do you get for the money?
            </h2>
            <p className="mb-4 leading-relaxed">
              It is easier to pay a bill you understand. Your SVS contributions buy you:
            </p>
            <ul className="mb-4 space-y-2 list-disc pl-6 leading-relaxed">
              <li>
                <strong>Health cover</strong> <span lang="de">(Krankenversicherung)</span> — your e-card, GP and specialist visits, hospital treatment, and cover
                for your dependent family members.
              </li>
              <li>
                <strong>Pension years</strong> <span lang="de">(Pensionsversicherung)</span> — by far the largest
                part of the bill. Every insured year counts towards your Austrian state pension, and
                towards the minimum insurance years you need to draw one at all.
              </li>
              <li>
                <strong>Accident cover</strong> <span lang="de">(Unfallversicherung)</span> — including accidents on the way to a client, for €12.95 a month.
              </li>
              <li>
                <strong>Self-employed provision</strong> <span lang="de">(Selbständigenvorsorge)</span> — 1.53 % goes into a fund in your name. It is yours, and you
                can draw it later.
              </li>
            </ul>
            <p className="mb-4 leading-relaxed">
              There are also benefits people often do not know they have: support during longer illness, maternity
              benefit, and health-prevention programmes. What applies to you depends on your situation, so it is
              worth asking rather than assuming.
            </p>

            {/* Low income */}
            <h2 id="low-income" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              What if I earn very little?
            </h2>
            <h3 className="text-xl font-display font-bold text-pstax-navy mt-6 mb-3">
              Below the limit as a <span lang="de">Neue:r Selbständige:r</span>
            </h3>
            <p className="mb-4 leading-relaxed">
              Stay under €6,613.20 profit in 2026 and no compulsory health or pension insurance applies. But there is
              a trap attached:
            </p>
            <Callout tone="orange" label="The 9.3 % surcharge">
              If your profit does go over the limit, you have to tell the SVS yourself — with an{' '}
              <span lang="de">Überschreitungserklärung</span>, either during the year or within <strong>eight weeks</strong>{' '}
              of your tax assessment. Miss that window and the SVS adds a surcharge of{' '}
              <strong>9.3 % on the contributions</strong>. It is one of the easiest penalties in the Austrian system
              to avoid, and one of the most commonly paid.
            </Callout>
            <h3 id="small-business-exemption" className="text-xl font-display font-bold text-pstax-navy mt-6 mb-3">
              The small-business exemption <span lang="de">(Kleinstunternehmerregelung)</span>
            </h3>
            <p className="mb-4 leading-relaxed">
              If you hold a trade licence but your business is genuinely small, you can apply to be exempted from
              health and pension insurance. For 2026 both limits must hold: <strong>profit no more than €6,613.20</strong>{' '}
              and <strong>turnover no more than €55,000</strong>.
            </p>
            <p className="mb-4 leading-relaxed">
              Two things people get wrong here. First, the exemption is only open to certain groups — mainly people who
              were compulsorily insured under the GSVG for no more than twelve months in the last five years (that
              is, new starters), older self-employed people (from 57, with further conditions), and parents receiving
              childcare benefit. Second, it starts <strong>at the earliest on 1 January of the year your application
              reaches the SVS</strong> — so an application by 31 December can still cover the current year (if you
              have not claimed any benefits that year), but never earlier years. Accident insurance of €12.95 a month
              stays compulsory in every case.
            </p>

            {/* Too high or too low */}
            <h2 id="too-high" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              The bill is too high — or too low. What can you do?
            </h2>
            <p className="mb-4 leading-relaxed">
              More than most people think. The SVS is far more flexible than its letters suggest, but nothing
              happens automatically.
            </p>
            <h3 className="text-xl font-display font-bold text-pstax-navy mt-6 mb-3">
              Lower the provisional contributions <span lang="de">(Herabsetzungsantrag)</span>
            </h3>
            <p className="mb-4 leading-relaxed">
              If this year will clearly be weaker than the reference year, you can apply to have the provisional
              contributions reduced. Useful when cash is tight — but remember what it means: a smaller bill now is a
              bigger reassessment later.
            </p>
            <p className="mb-4 leading-relaxed">
              It works the other way too: if you apply in the SVS portal to <strong>raise</strong> the provisional
              base <span lang="de">(Hinaufsetzung)</span>, you see the new, higher quarterly amount — and the SVS
              immediately back-charges the difference for <strong>all quarters of the current year that have already
              passed</strong>. You then have to pay a large sum at once. That can make sense, but doing it in the
              fourth quarter without being aware of this is risky. A voluntary prepayment, by contrast, does not change
              your bills.
            </p>
            <h3 id="year-end-check" className="text-xl font-display font-bold text-pstax-navy mt-6 mb-3">
              Prepay voluntarily before 31 December
            </h3>
            <p className="mb-4 leading-relaxed">
              Before the year ends you can voluntarily transfer an amount to the SVS that roughly matches the expected
              back-payment for the current year. Here is what happens:
            </p>
            <ul className="mb-4 space-y-2 list-disc pl-6 leading-relaxed">
              <li>
                The payment is <strong>deductible as a business expense in the year you pay it</strong>, as long as
                it is based on a careful estimate (EStR Rz 4623) — also on top of flat-rate expenses.
              </li>
              <li>
                It sits as a <strong>credit</strong> on your SVS account. You cannot earmark it: the SVS
                automatically sets it off against your next quarterly bills.
              </li>
              <li>
                Your bills themselves do not change — unlike with a Hinaufsetzung, where the SVS immediately
                back-charges the difference for the quarters already past, all at once. The base only goes up once the
                SVS settles the year with your tax assessment.
              </li>
            </ul>
            <Callout tone="orange" label="Important">
              After a prepayment, <strong>keep paying your quarterly bills as usual</strong>. Otherwise the credit is
              used up before the back-payment arrives. Having the credit refunded would count as business income
              again — we advise against it.
            </Callout>
            <p className="mb-4 leading-relaxed">
              To be honest: in most cases a prepayment is a <strong>shift, not a saving</strong> — for tax and for SVS.
              Contributions count towards the contribution base in the year they are charged (§ 25 GSVG), and the
              deduction simply comes earlier. There is a real tax advantage when you pay in the year with the higher
              income and marginal rate — for example if you are closing or pausing the business, will earn much less
              next year, are going on parental leave or moving abroad.
            </p>
            <p className="mb-4 leading-relaxed">
              The main benefit is a different one: you know what is coming, and nothing surprises you.
            </p>
            <p className="mb-4 leading-relaxed">
              It usually makes little sense if you have just started or have very low revenue (under about €10,000
              revenue or €6,613.20 profit), if you expect much higher profits later, if this year's profit is already
              low because of large back-payments for earlier years, or if you simply cannot afford it. With a profit
              under about €20,000 (marginal rate of 20 % or less) it is rarely urgent. One trap when comparing: what
              you paid the SVS this year may include back-payments for earlier years — only contributions{' '}
              <em>for</em> the current year count.
            </p>
            <p className="mb-4 leading-relaxed">
              How we handle it with our clients: you keep your bookkeeping up to date. From early November, once the
              fourth-quarter bill is visible in the SVS portal, we pull your SVS data and send you an individual
              estimate with the payment details (SVS IBAN, social-security number, amount). If you have not heard from
              us by mid-December, there is nothing to do. The short version for clients:{' '}
              <Link to={TOPIC.en.shortPath} className="text-pstax-link hover:underline font-semibold">
                SVS prepayment in short
              </Link>
              .
            </p>
            <h3 className="text-xl font-display font-bold text-pstax-navy mt-6 mb-3">
              Instalments and deferral <span lang="de">(Ratenzahlung, Stundung)</span>
            </h3>
            <p className="mb-4 leading-relaxed">
              If a back-payment lands that you cannot cover, the SVS will usually agree to instalments. You have to
              ask, in writing, and ideally before the due date. Back-payments from your first three years of
              self-employment can also be spread over twelve quarters instead of four, on request.
            </p>

            {/* Tax */}
            <h2 id="tax" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              SVS and income tax belong together
            </h2>
            <p className="mb-4 leading-relaxed">
              These two are often handled as separate problems. They are not.
            </p>
            <p className="mb-4 leading-relaxed">
              SVS contributions are business expenses — also on top of flat-rate expenses (Basis- or
              Kleinunternehmerpauschalierung) — so they reduce your profit, and therefore your income tax, in the
              year you pay them (with cash-basis accounting). That has practical consequences:
            </p>
            <ul className="mb-4 space-y-2 list-disc pl-6 leading-relaxed">
              <li>A large SVS back-payment in 2028 reduces your 2028 tax bill.</li>
              <li>Paying on 28 December instead of 2 January moves the deduction by a full year.</li>
              <li>Lowering your SVS contributions raises your taxable profit in the same year.</li>
            </ul>
            <p className="mb-4 leading-relaxed">
              Plan them separately and you will almost always pay more than you need to. See also our guide to income
              tax for the self-employed.
              <SoonBadge />
            </p>

            {/* Mistakes */}
            <h2 id="mistakes" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Five mistakes we see most often
            </h2>
            <ul className="mb-4 space-y-3 list-disc pl-6 leading-relaxed">
              <li>
                <strong>Treating the first bills as the real cost.</strong> They are a deposit, not the price.
              </li>
              <li>
                <strong>Not setting money aside.</strong> A workable rule of thumb: put aside roughly a third of your
                profit for tax and SVS together, in a separate account.
              </li>
              <li>
                <strong>Missing the <span lang="de">Überschreitungserklärung</span>.</strong> Eight weeks after the
                assessment, then 9.3 % on top.
              </li>
              <li>
                <strong>Reducing the contributions without a plan.</strong> It moves the problem, it does not solve
                it.
              </li>
              <li>
                <strong>Leaving the letters unopened.</strong> SVS post is in German and it contains deadlines.
                Forward it to someone who reads it the day it arrives.
              </li>
            </ul>

            {/* Moved here */}
            <h2 id="moved-here" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              If you moved to Austria recently
            </h2>
            <p className="mb-4 leading-relaxed">
              You are not missing something obvious. The SVS system is not intuitive for Austrians either — the
              difference is that they usually have someone in the family who has been through it.
            </p>
            <p className="mb-4 leading-relaxed">
              Three things worth knowing early. The letters come in German, with deadlines that run from the delivery
              date. Terms like <span lang="de">Nachbemessung</span>, <span lang="de">Beitragsgrundlage</span> and{' '}
              <span lang="de">Vorschreibung</span> have no clean English equivalents, so a literal translation will
              not tell you what to do. And if you were insured in another EU country before, or you still have
              clients there, the sequence of your registrations can change what you owe — that is worth a
              conversation before you register, not after.
            </p>
            <p className="mb-8 leading-relaxed">
              At PSTax, more than half of our clients are not native German speakers. You forward the letter, we tell
              you in English what it says and what happens next.
            </p>

            <figure className="mb-8">
              <picture>
                <source srcSet="/pstax-team-vienna.webp" type="image/webp" />
                <img
                  src="/pstax-team-vienna.jpg"
                  alt="The PSTax team in Vienna, working together on a client case at a shared desk."
                  width={1400}
                  height={934}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-auto rounded-2xl border border-pstax-border"
                />
              </picture>
            </figure>

            {/* FAQ */}
            <h2 id="faq" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Frequently asked questions
            </h2>
            <div className="space-y-3 mb-10">
              {FAQS_EN.map((faq) => (
                <details key={faq.q} className="group bg-white border border-pstax-border rounded-2xl overflow-hidden">
                  <summary className="cursor-pointer list-none px-5 py-4 min-h-[44px] flex items-center justify-between gap-4 font-semibold text-pstax-navy">
                    <span>{faq.q}</span>
                    <span aria-hidden="true" className="shrink-0 text-pstax-blue transition-transform group-open:rotate-180">
                      ▾
                    </span>
                  </summary>
                  <div className="px-5 pb-5 text-pstax-gray leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>

            {/* Sources */}
            <h2 id="sources" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Official sources
            </h2>
            <p className="mb-4 leading-relaxed">
              Every figure on this page comes from an official Austrian source. All of them are in German — that is
              part of the problem this page exists to solve.
            </p>
            <ul className="mb-4 space-y-4 leading-relaxed">
              <li>
                <ExternalLink
                  href="https://www.wko.at/entlohnung/beitragswesen-selbstaendige-2026"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  WKO — Beitragswesen Selbständige 2026
                </ExternalLink>
                <br />
                <span className="text-sm text-pstax-gray">
                  Rates, minimum and maximum contribution bases for 2026.
                </span>
              </li>
              <li>
                <ExternalLink
                  href="https://www.wko.at/entlohnung/gewerbliche-sozialversicherung-fuer-neugruender-2026"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  WKO — Gewerbliche Sozialversicherung für Neugründer 2026
                </ExternalLink>
                <br />
                <span className="text-sm text-pstax-gray">
                  The fixed contribution base in the first years, and what is and is not reassessed.
                </span>
              </li>
              <li>
                <ExternalLink
                  href="https://www.wko.at/sozialversicherung/gewerbliche-sozialversicherungsbeitraege-grundlage"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  WKO — Endgültige Beitragsgrundlage
                </ExternalLink>
                <br />
                <span className="text-sm text-pstax-gray">
                  How the final contribution base is determined, and how the reassessment works.
                </span>
              </li>
              <li>
                <ExternalLink
                  href="https://www.svs.at/cdscontent/?contentid=10007.816635"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  SVS — Endgültige Berechnung in den ersten drei Jahren
                </ExternalLink>
                <br />
                <span className="text-sm text-pstax-gray">What is reassessed in the first years, and how the back-payment is split across quarters.</span>
              </li>
              <li>
                <ExternalLink
                  href="https://www.svs.at/cdscontent/?contentid=10007.846813"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  SVS — Kleinunternehmer
                </ExternalLink>
                <br />
                <span className="text-sm text-pstax-gray">Limits, eligible groups, and from when the exemption applies.</span>
              </li>
              <li>
                <ExternalLink
                  href="https://www.svs.at/"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  SVS — Sozialversicherungsanstalt der Selbständigen
                </ExternalLink>
                <br />
                <span className="text-sm text-pstax-gray">
                  Forms, deadlines, the online portal and the insurance threshold rules.
                </span>
              </li>
            </ul>

            {/* CTA */}
            <div className="mt-14 bg-pstax-navy text-white rounded-3xl p-8 sm:p-10">
              <h2 className="text-2xl sm:text-3xl font-display font-bold mb-3">
                Not sure where you stand with the SVS?
              </h2>
              <p className="text-white/75 leading-relaxed mb-6 max-w-2xl">
                Book a free 30-minute Tax Check. We look at your actual situation, tell you what is coming, and you
                decide afterwards whether you want to work with us. No obligation.
              </p>
              <div className="flex flex-wrap gap-4 mb-6">
                <Link to="/en/#fit-check" className="btn-accent">
                  Book your free Tax Check
                </Link>
                <Link to="/en/#calculator" className="btn-outline !border-white !text-white hover:!bg-white/10">
                  Try the tax calculator
                </Link>
              </div>
              <p className="text-xs text-white/50">
                Fixed annual pricing from €800. 600+ clients in Austria. All correspondence in English
                or German — your choice.
              </p>
            </div>

            {/* Author box */}
            <div id="author" className="mt-10 bg-white border border-pstax-border rounded-2xl p-6 sm:p-8 flex gap-5 items-start">
              <picture className="shrink-0">
                <source srcSet="/pascal-schraml.webp" type="image/webp" />
                <img
                  src="/pascal-schraml.jpg"
                  alt="Pascal Schraml, Austrian certified tax advisor and founder of PSTax."
                  width={76}
                  height={76}
                  loading="lazy"
                  decoding="async"
                  className="w-[76px] h-[76px] rounded-full object-cover"
                />
              </picture>
              <div>
              <p className="font-display font-bold text-pstax-navy text-lg mb-0.5">Reviewed by Pascal Schraml</p>
              <p className="text-sm text-pstax-gray mb-3">
                Austrian certified tax advisor (<span lang="de">Steuerberater</span>), founder of PSTax
              </p>
              <p className="text-sm text-pstax-gray leading-relaxed">
                Pascal has advised self-employed clients in Austria since 2023 and reviews every guide on this site
                for technical accuracy. PSTax works with 600+ clients, more than half of them
                non-native German speakers.
              </p>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="mt-8 p-6 bg-pstax-bg rounded-2xl border border-pstax-border">
              <p className="text-xs text-pstax-gray leading-relaxed">
                <strong className="text-pstax-navy">About this page.</strong> All figures apply to the 2026
                contribution year and change annually. This guide gives general information about Austrian social
                insurance for the self-employed; it is not individual advice and cannot replace it, because the
                right answer depends on your specific situation. Reviewed by Pascal Schraml, Austrian certified tax
                advisor (<span lang="de">Steuerberater</span>), PSTax Steuerberatung FlexCo.
              </p>
            </div>

            <MoreTopics currentKey={TOPIC.key} lang="en" />
            <FitCheckBox lang="en" />
          </article>
      </div>
    </div>
  );
};
